import { useEffect, useState } from 'react';

import { Flex } from '@revenuemarket/cds';
import { Colors, theme, typographyStyle } from '@revenuemarket/design';
import { ArrowLeft } from '@revenuemarket/ui';
import { Input, Segmented, Typography } from 'antd';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';

import { CategoryTreeItem } from '~apis/categories';
import { FlattenTreeResult, flattenTree } from '~components/ui/HorizontalTreeTable/Table';
import { TreeNodeId, addNode, buildIdToNodeMap } from '~components/ui/HorizontalTreeTable/utils';
import useGetAlertBannerInfo from '~components/ui/InfoAlertBanner/useGetAlertBannerInfo';
import { useCategoriesTree } from '~hooks/queries/categories/query';
import { useDemoAwareCompanyId } from '~hooks/useDemoAwareCompanyId';
import { MIXPANEL_EVENT } from '~libs/mixpanel/event';
import { CashFlowType } from '~models/cashFlow';
import { mixpanel } from '~pages/_app';
import { PAGE_NAME } from '~pages/clobe/label-management';

import CategoryLabelTreeTable from './CategoryLabeTreeTable';
import CategorySearchList from './CategorySearchList';
import AddNode from './common/component/AddNode';
import FoldToggleButton from './common/component/FoldButton';
import useCategoryLabelTreeStore from './store/categoryLabelTreeStore';

interface Props {
  activeItem?: FlattenTreeResult<CategoryTreeItem>;
  setActiveItem: (item: FlattenTreeResult<CategoryTreeItem>) => void;
}

const CategoryLabelManagement: React.FC<Props> = ({ activeItem, setActiveItem }) => {
  const { companyId } = useDemoAwareCompanyId();
  const { data } = useCategoriesTree({ companyId });
  const { setCategoryLabelTree, categoryTree } = useCategoryLabelTreeStore();
  const categories = data?.Data?.data?.tree?.children;
  const flattenedTree = flattenTree(categories ?? []).filter(
    (item) => !item.value?.contraCategoryId,
  );

  const [mode, setMode] = useState<'view' | 'search'>('view');
  const [cashFlowType, setCashFlowType] = useState<CashFlowType | 'all'>();
  const { bannerHeight } = useGetAlertBannerInfo();
  const [foldedAll, setFoldedAll] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  const handleAddNode = (parentId: TreeNodeId, categoryLabelKey: string, idToNodeMap: any) => {
    const newNode = {
      key: `${parentId}-temp`,
      status: 'new',
      value: {
        cashFlowType: flattenedTree.find((item) => item.key === parentId)?.value?.cashFlowType,
        id: `${parentId}-temp`,
        categoryLabelKey,
        label: <AddNode />,
      },
      children: [],
    };

    addNode(parentId, newNode, idToNodeMap);
    setCategoryLabelTree([...categoryTree]);
  };

  useEffect(() => {
    if (categories?.length) {
      const idToNodeMap = buildIdToNodeMap(categories);
      const categoryTarget = categories;
      const flattenedTree = flattenTree(categoryTarget);

      flattenedTree.forEach((node) => {
        const isLeaf = !node.children?.length && node.value?.name && node.value?.categoryLabelKey;
        const hasOneChildren =
          node.children[0]?.status !== 'new' &&
          node.value?.type === 'COMPANY' &&
          !node.children.map((child) => child.status).includes('new') &&
          // 첫번째 children인지
          node.groupRowIndex === 0;

        if (isLeaf) {
          handleAddNode(node.key, node?.value?.categoryLabelKey as string, idToNodeMap);
        }

        if (hasOneChildren && node.parentId) {
          handleAddNode(node.parentId, node?.value?.categoryLabelKey as string, idToNodeMap);
        }
      });

      setCategoryLabelTree([...categoryTarget]);
    }
  }, [categories]);

  return (
    <Flex
      direction="column"
      gap={10}
      tabIndex={0}
      style={{
        outline: 'none',
        width: `calc(100% - 180px)`,
      }}
    >
      <Typography.Text style={{ ...typographyStyle['14_600'], color: Colors.gray800 }}>
        계정
      </Typography.Text>
      <Flex align="center" justify="space-between">
        <Flex align="center" gap={8}>
          <Segmented
            style={{
              backgroundColor: theme.colorTokens.bgWeak,
            }}
            options={[
              {
                label: '전체',
                value: 'all',
              },
              {
                label: '입금',
                value: CashFlowType.IN,
              },
              {
                label: '출금',
                value: CashFlowType.OUT,
              },
            ]}
            onChange={(value) => setCashFlowType(value as CashFlowType)}
            defaultValue="all"
            value={cashFlowType}
          />
          <Flex gap={4} align="center">
            {searchValue ? (
              <ArrowLeft
                onClick={() => {
                  setSearchValue('');
                  setMode?.('view');
                }}
                style={{
                  cursor: 'pointer',
                }}
              />
            ) : null}
            <Input.Search
              placeholder="계정 이름 검색"
              value={searchValue}
              onChange={(e) => {
                const { value } = e.target;
                setSearchValue(value);
                mixpanel.track(MIXPANEL_EVENT.GLOBAL.SEARCH, {
                  page: PAGE_NAME,
                  type: 'label-management',
                  searchParam: value,
                });
                if (!value) {
                  setMode?.('view');
                }

                if (mode !== 'search' && value) {
                  setMode?.('search');
                }
              }}
            />
          </Flex>
        </Flex>
        <FoldToggleButton onToggleFold={setFoldedAll} />
      </Flex>
      <OverlayScrollbarsComponent
        style={{
          overflowX: 'scroll',
          width: '100%',
          height: `calc(100vh - ${bannerHeight + 152}px)`,
        }}
        id="horizontalTreeTableScrollWrapper"
      >
        {mode === 'view' ? (
          <CategoryLabelTreeTable
            treeData={categoryTree}
            setMode={setMode}
            mode={mode}
            activeItem={activeItem}
            setActiveItem={setActiveItem}
            foldedAll={foldedAll}
            cashFlowType={cashFlowType === 'all' ? undefined : cashFlowType}
          />
        ) : (
          <CategorySearchList
            onSelectFinish={(key) => {
              setMode('view');
              setSearchValue('');
              setTimeout(() => {
                const node = document.querySelector(`[data-id="${key}"]`);

                if (node) {
                  node.scrollIntoView({
                    block: 'center',
                  });
                }
              }, 100);
            }}
            disableActionOnInactiveItem={false}
            treeList={flattenedTree}
            searchValue={searchValue}
            setActiveItem={setActiveItem}
            cashFlowType={cashFlowType === 'all' ? undefined : cashFlowType}
          />
        )}
      </OverlayScrollbarsComponent>
    </Flex>
  );
};

export default CategoryLabelManagement;
